import React from 'react';
import { cn } from '@/lib/cn';
import { IconBroom, IconStarFilled } from '@repo/svgs';
import { MaskedText } from './MaskedText';

export interface RatingCellProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The rating category title */
  title: string;
  /** The rating value to display */
  rating: number;
  /** Optional icon component - defaults to IconBroom (24px recommended) */
  icon?: React.ComponentType<{ className?: string }>;
  /** Whether to mask the rating */
  isMasked?: boolean;
}

/**
 * RatingCell - A reusable component for displaying rating categories with icon, title, and star rating
 * Used in "Building ratings" sections
 */
export function RatingCell({
  title,
  rating,
  icon: Icon = IconBroom,
  className,
  isMasked = false,
  ...props
}: RatingCellProps) {
  return (
    <div
      className={cn(
        // Base styles - matches Figma design
        'bg-primary flex items-center gap-4 px-2 py-3',
        // Responsive: full width on mobile, grows on desktop
        'basis-0 grow min-w-0',
        className,
      )}
      {...props}
    >
      {/* Icon - 24px */}
      <div className="shrink-0 w-6 h-6">
        <Icon className="w-full h-full icon-secondary" />
      </div>

      {/* Content container */}
      <div className="flex flex-row md:flex-col justify-between gap-1 flex-1 min-w-0">
        {/* Title - Body 2 */}
        <p className="text-body-2 text-primary">{title}</p>

        {/* Rating container - Star + Rating */}
        <div className="flex items-center gap-1">
          <IconStarFilled
            className={cn('w-4 h-4 icon-rating', rating > 0 ? 'icon-rating' : 'icon-tertiary')}
          />
          <div className="text-body-2-high text-primary">
            {isMasked ? <MaskedText className="w-8 h-4" /> : rating > 0 ? rating.toFixed(1) : '-'}
          </div>
        </div>
      </div>
    </div>
  );
}
